import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Device } from "@twilio/voice-sdk";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CallCtx = createContext(null);

// ─── Twilio Voice SDK error-code translation ─────────────────────────────
// Codes documented at: https://www.twilio.com/docs/api/errors and the
// Voice SDK's own error map. We surface a plain-English cause + a next
// action so agents don't just see "31005: connection error".
const TWILIO_ERROR_MAP = {
  20101: "Twilio access token is invalid or expired. Sign out and back in.",
  20104: "Twilio access token expired mid-call. Reconnecting…",
  31000: "Twilio SDK error. Please try again.",
  31005: "Connection to Twilio dropped. Check your network and try again.",
  31009: "Twilio transport error — usually a network firewall blocking WebRTC. Try a different network.",
  31201: "Microphone access was denied. Enable microphone permission in your browser and reload.",
  31202: "No microphone was found on this device.",
  31208: "Microphone is already in use by another application.",
  31402: "Twilio couldn't fetch our TwiML. This usually means the webhook URL is wrong or the backend returned an error. Contact support.",
  31404: "Twilio returned 404 for the TwiML endpoint. The TwiML App is misconfigured — contact your administrator.",
  31480: "Twilio couldn't reach the TwiML endpoint (5xx). The backend may be down.",
  53000: "WebRTC connection failed. Check your network.",
  // ── Server-side rejections (Twilio blocks before reaching our webhook) ──
  13223: "Twilio rejected the number format. Use E.164, e.g. +15551234567.",
  13224: "Twilio can't route to this number (invalid/unsupported destination).",
  13225: "Twilio blocked this call. Common causes: (1) your Trust Hub Primary Customer Profile must be BUSINESS type (Individual profiles can't dial +1 destinations from non-US accounts), or (2) the destination is flagged by Twilio's fraud system. Verify at console.twilio.com → Trust Hub, or contact Twilio Support.",
  13227: "Twilio Geo Permissions block this destination country. Enable it at Twilio Console → Voice → Geo Permissions.",
  32017: "Twilio can't route to that country. Enable the region in Twilio Console → Voice → Geo Permissions.",
};
function explainTwilioError(e) {
  const code = e?.code || e?.causes?.[0] || null;
  const msg  = TWILIO_ERROR_MAP[code] || e?.message || "Unknown Twilio error";
  return code ? `[${code}] ${msg}` : msg;
}

export function CallProvider({ children }) {
  const { user } = useAuth();
  const [activeCall, setActiveCall] = useState(null);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [recording, setRecording] = useState(false);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [deviceReady, setDeviceReady] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [lastCallError, setLastCallError] = useState(null); // user-visible failure reason
  const tickRef = useRef(null);
  const deviceRef = useRef(null);
  const callRef = useRef(null);

  // ── Diagnostic beacon ──────────────────────────────────────────────────
  // Fires a POST /api/twilio/client-event for every Voice SDK event so a
  // support engineer can correlate the browser timeline with the backend
  // /twilio/status callback and the Twilio Console. Never throws — a beacon
  // failure must not break call behaviour.
  const beacon = useCallback((event, callSid, error, extra) => {
    const payload = {
      event,
      timestamp: new Date().toISOString(),
      call_sid: callSid || null,
      error: error ? {
        code:    error.code ?? error?.originalError?.code ?? null,
        message: (error.message ?? String(error)) || null,
        name:    error.name || null,
        explanation: error.explanation || null,
      } : null,
      extra: extra || {},
    };
    // Fire-and-forget. Absolutely no `await` — we don't want to block SDK
    // event handlers on network latency.
    try {
      api.post("/twilio/client-event", payload).catch(() => { /* noop */ });
    } catch { /* noop */ }
    // Also log to browser console for local debugging.
    if (event.endsWith(".error") || event === "device.unregistered") {
      console.error("[Twilio beacon]", event, payload);
    } else {
      console.info("[Twilio beacon]", event, payload);
    }
  }, []);

  // Register Twilio Device once after login (when token endpoint is reachable).
  // Skipped entirely for accounts that have no org_id (Platform Owner, unattached
  // staff) — those users can never receive/place customer calls, and hitting the
  // token endpoint just produces 401 noise on every page load.
  //
  // Reconnect: iter123 — WebRTC transport dies when browsers throttle
  // backgrounded tabs, mobile devices sleep, or network flaps (VPN drop,
  // Wi-Fi handoff). Twilio surfaces this as [31009] "transport error" or
  // [20104] "token expired". Instead of showing a scary toast and staying
  // dead, we now:
  //   • rebuild the Device on `visibilitychange → visible` if not ready
  //   • rebuild on `online` after browser regains network
  //   • rebuild automatically on fatal Device errors (31005 / 31009 / 20104)
  const reconnectingRef = useRef(false);
  const reconnectDevice = useCallback(async () => {
    if (!user?.org_id || reconnectingRef.current) return;
    reconnectingRef.current = true;
    try {
      // Tear down the old device (best-effort; race-safe).
      try { deviceRef.current?.destroy(); } catch { /* ignore */ }
      deviceRef.current = null;
      setDeviceReady(false);
      // Ask backend for a fresh access token — old one may already be expired.
      const { data } = await api.get("/twilio/token");
      if (!data?.token) throw new Error("Twilio token missing from server");
      const dev = new Device(data.token, { codecPreferences: ["opus", "pcmu"], logLevel: 1 });
      dev.on("registered", () => {
        setDeviceReady(true);
        beacon("device.registered", null, null, { identity: data.identity, reconnected: true });
      });
      dev.on("unregistered", () => {
        beacon("device.unregistered", null, null, { identity: data.identity });
      });
      dev.on("error", (e) => {
        const explained = explainTwilioError(e);
        console.error("[Twilio Device error → auto-retry]", e, explained);
        setLastCallError(explained);
        beacon("device.error", null, e, { explained });
        // Fatal transport / token errors → schedule reconnect.
        const code = e?.code;
        if ([31005, 31009, 20104, 31000].includes(code)) {
          setTimeout(reconnectDevice, 2000);
        }
      });
      dev.on("incoming", (call) => {
        setIncomingCall(call);
        beacon("device.incoming", call?.parameters?.CallSid, null, { from: call?.parameters?.From });
        call.on("disconnect", () => { beacon("call.disconnect", call?.parameters?.CallSid, null, { origin: "incoming" }); setIncomingCall(null); });
        call.on("cancel",     () => { beacon("call.cancel",     call?.parameters?.CallSid, null, { origin: "incoming" }); setIncomingCall(null); });
        call.on("error",      (e) => beacon("call.error",       call?.parameters?.CallSid, e,    { origin: "incoming" }));
        call.on("warning", (name, data) => beacon("call.warning", call?.parameters?.CallSid, null, { warning: name, data, origin: "incoming" }));
        call.on("warning-cleared", (name) => beacon("call.warning_cleared", call?.parameters?.CallSid, null, { warning: name, origin: "incoming" }));
        call.on("reconnecting", (e) => { toast.info("Call reconnecting…"); beacon("call.reconnecting", call?.parameters?.CallSid, e, { origin: "incoming" }); });
        call.on("reconnected", () => { toast.success("Call reconnected"); beacon("call.reconnected", call?.parameters?.CallSid, null, { origin: "incoming" }); });
      });
      await dev.register();
      deviceRef.current = dev;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      console.error("[Twilio Device reconnect failed]", detail);
      setLastCallError(detail);
    } finally {
      reconnectingRef.current = false;
    }
  }, [user?.org_id, beacon]);

  useEffect(() => {
    if (!user?.org_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/twilio/token");
        if (cancelled || !data?.token) {
          if (!data?.token) {
            const msg = "Twilio access token missing from server response.";
            console.error("[Twilio Device init] " + msg);
            toast.error("Softphone unavailable: " + msg);
            setLastCallError(msg);
          }
          return;
        }
        const dev = new Device(data.token, { codecPreferences: ["opus", "pcmu"], logLevel: 1 });
        dev.on("registered", () => {
          console.info("[Twilio Device] registered as", data.identity);
          setDeviceReady(true);
          beacon("device.registered", null, null, { identity: data.identity });
          // iter138: proactively enumerate + log Twilio audio devices so
          // when a user reports "no audio", support can immediately see
          // whether input/output devices were even detected. Also try to
          // set default speaker on browsers that support it — required
          // for reliable remote-audio playback in Chrome.
          try {
            const inputs = dev.audio?.availableInputDevices;
            const outputs = dev.audio?.availableOutputDevices;
            const inputCount = inputs ? (inputs.size ?? Object.keys(inputs).length) : 0;
            const outputCount = outputs ? (outputs.size ?? Object.keys(outputs).length) : 0;
            console.info("[Twilio Device] audio devices — inputs=", inputCount, "outputs=", outputCount);
            if (inputCount === 0) beacon("device.no_input", null, null, {});
            if (outputCount === 0) beacon("device.no_output", null, null, {});
            if (dev.audio?.speakerDevices?.set && outputs) {
              const first = outputs.keys ? outputs.keys().next().value : null;
              if (first) dev.audio.speakerDevices.set([first]).catch((e) =>
                console.warn("[Twilio Device] speakerDevices.set failed", e?.message));
            }
          } catch (err) {
            console.warn("[Twilio Device] audio device probe failed", err?.message);
          }
        });
        dev.on("unregistered", () => {
          beacon("device.unregistered", null, null, { identity: data.identity });
        });
        dev.on("error", (e) => {
          const explained = explainTwilioError(e);
          console.error("[Twilio Device error]", e, "→", explained);
          setLastCallError(explained);
          beacon("device.error", null, e, { explained });
          // Fatal transport / token errors → schedule reconnect. Only toast
          // for user-actionable errors (mic denied, geo blocked); silent
          // transport hiccups just self-heal.
          const code = e?.code;
          if ([31005, 31009, 20104, 31000].includes(code)) {
            console.info("[Twilio Device] transport error, auto-reconnecting…");
            setTimeout(reconnectDevice, 2000);
          } else {
            toast.error("Softphone error: " + explained);
          }
        });
        dev.on("incoming", (call) => {
          setIncomingCall(call);
          beacon("device.incoming", call?.parameters?.CallSid, null, { from: call?.parameters?.From });
          call.on("disconnect", () => {
            beacon("call.disconnect", call?.parameters?.CallSid, null, { origin: "incoming" });
            setIncomingCall(null);
          });
          call.on("cancel", () => {
            beacon("call.cancel", call?.parameters?.CallSid, null, { origin: "incoming" });
            setIncomingCall(null);
          });
          call.on("error", (e) => {
            beacon("call.error", call?.parameters?.CallSid, e, { origin: "incoming" });
          });
          // iter138: mirror media diagnostics on inbound calls.
          call.on("warning", (name, data) => beacon("call.warning", call?.parameters?.CallSid, null, { warning: name, data, origin: "incoming" }));
          call.on("warning-cleared", (name) => beacon("call.warning_cleared", call?.parameters?.CallSid, null, { warning: name, origin: "incoming" }));
          call.on("reconnecting", (e) => { toast.info("Call reconnecting…"); beacon("call.reconnecting", call?.parameters?.CallSid, e, { origin: "incoming" }); });
          call.on("reconnected", () => { toast.success("Call reconnected"); beacon("call.reconnected", call?.parameters?.CallSid, null, { origin: "incoming" }); });
        });
        await dev.register();
        deviceRef.current = dev;
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Unknown error";
        console.error("[Twilio Device] init failed:", e?.response?.status, detail);
        toast.error("Softphone unavailable: " + detail);
        setLastCallError(detail);
      }
    })();
    // Auto-reconnect on visibility + network changes. Backgrounded tabs get
    // their WebRTC transport paused by the browser; when the user returns
    // we may still show `deviceReady=true` but the underlying socket is
    // dead. Force a re-register whenever we come back to the foreground.
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !reconnectingRef.current) {
        // Give the browser a beat to un-throttle before we probe.
        setTimeout(() => {
          if (!deviceRef.current || !deviceReady) reconnectDevice();
        }, 500);
      }
    };
    const onOnline = () => { if (!reconnectingRef.current) reconnectDevice(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      try { deviceRef.current?.destroy(); } catch { /* destroy on unmount may race with init — safe to ignore */ }
    };
  }, [user?.org_id, beacon, reconnectDevice, deviceReady]);

  useEffect(() => {
    if (activeCall && !held) {
      tickRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
      return () => clearInterval(tickRef.current);
    }
  }, [activeCall, held]);

  const startCall = async ({ to_number, to_name }) => {
    setDuration(0); setMuted(false); setHeld(false); setRecording(false);
    setLastCallError(null);
    setActiveCall({ to_number, to_name, started_at: new Date().toISOString() });
    setDialerOpen(true);
    if (!deviceRef.current || !deviceReady) {
      const msg = deviceRef.current
        ? "Softphone is initializing — try again in a moment."
        : "Softphone is not available. " + (lastCallError || "Reload the page to retry.");
      console.error("[startCall] device not ready:", { hasDevice: !!deviceRef.current, deviceReady, lastCallError });
      toast.error("Cannot place call: " + msg);
      setLastCallError(msg);
      setActiveCall(null);
      return;
    }
    try {
      console.info("[startCall] connecting to", to_number);
      const call = await deviceRef.current.connect({ params: { To: to_number } });
      callRef.current = call;
      call.on("accept", () => {
        console.info("[Twilio Call] accepted, sid=", call.parameters?.CallSid);
        setActiveCall((c) => ({ ...c, sid: call.parameters?.CallSid }));
        beacon("call.accept", call?.parameters?.CallSid, null, { origin: "outbound", to_number });
      });
      call.on("disconnect", () => {
        console.info("[Twilio Call] disconnected");
        beacon("call.disconnect", call?.parameters?.CallSid, null, { origin: "outbound" });
        setActiveCall(null); setDuration(0); callRef.current = null;
      });
      call.on("cancel", () => {
        beacon("call.cancel", call?.parameters?.CallSid, null, { origin: "outbound" });
      });
      call.on("error", (e) => {
        const explained = explainTwilioError(e);
        console.error("[Twilio Call error]", e, "→", explained);
        toast.error("Call failed: " + explained);
        setLastCallError(explained);
        beacon("call.error", call?.parameters?.CallSid, e, { origin: "outbound", explained });
        setActiveCall(null);
      });
      // iter138: media-quality visibility for outbound calls. Twilio SDK
      // emits `warning` for high-rtt, high-jitter, low-mos, packet loss,
      // and `reconnecting` when media path drops. These are the events
      // that fire during "connected but no audio" incidents — before the
      // caller notices something is wrong.
      call.on("warning", (name, data) => {
        console.warn("[Twilio Call warning]", name, data);
        beacon("call.warning", call?.parameters?.CallSid, null, { warning: name, data });
      });
      call.on("warning-cleared", (name) => {
        console.info("[Twilio Call warning-cleared]", name);
        beacon("call.warning_cleared", call?.parameters?.CallSid, null, { warning: name });
      });
      call.on("reconnecting", (e) => {
        console.warn("[Twilio Call reconnecting]", e);
        beacon("call.reconnecting", call?.parameters?.CallSid, e, { origin: "outbound" });
        toast.info("Call reconnecting…");
      });
      call.on("reconnected", () => {
        console.info("[Twilio Call reconnected]");
        beacon("call.reconnected", call?.parameters?.CallSid, null, { origin: "outbound" });
        toast.success("Call reconnected");
      });
      call.on("ringing", (hasEarlyMedia) => {
        beacon("call.ringing", call?.parameters?.CallSid, null, { hasEarlyMedia });
      });
      // Extra safety: if the call ends before "accept" fires, report the
      // reason instead of silently returning to idle.
      call.on("cancel", () => {
        console.warn("[Twilio Call] cancelled before accept");
        toast.error("Call cancelled before it connected.");
        setActiveCall(null);
      });
      call.on("reject", () => {
        console.warn("[Twilio Call] rejected");
        toast.error("Call was rejected.");
        setActiveCall(null);
      });
    } catch (e) {
      const explained = explainTwilioError(e);
      console.error("[startCall] Twilio connect failed:", e, "→", explained);
      toast.error("Cannot place call: " + explained);
      setLastCallError(explained);
      setActiveCall(null);
    }
  };

  const acceptIncoming = () => {
    if (!incomingCall) return;
    incomingCall.accept();
    callRef.current = incomingCall;
    const params = incomingCall.parameters || {};
    setActiveCall({ to_number: params.From || "Unknown", to_name: "Incoming", sid: params.CallSid, started_at: new Date().toISOString() });
    setDialerOpen(true);
    setIncomingCall(null);
    incomingCall.on("disconnect", () => { setActiveCall(null); setDuration(0); callRef.current = null; });
  };

  const rejectIncoming = () => {
    if (!incomingCall) return;
    incomingCall.reject();
    setIncomingCall(null);
  };

  const endCall = async () => {
    try { callRef.current?.disconnect(); } catch (e) { console.debug("[twilio] disconnect on already-ended call:", e?.message); }
    setActiveCall(null); setDuration(0); callRef.current = null;
  };

  const setMutedReal = (m) => {
    setMuted(m);
    try { callRef.current?.mute(m); } catch (e) { console.debug("[twilio] mute toggle failed:", e?.message); }
  };

  const sendDigits = (d) => {
    try { callRef.current?.sendDigits(d); } catch (e) { console.debug("[twilio] sendDigits failed:", e?.message); }
  };

  const transferCall = async (target, mode = "cold") => {
    if (!activeCall?.sid) { toast.error("No active Twilio call to transfer"); return; }
    try {
      const { data } = await api.post("/twilio/transfer", { call_sid: activeCall.sid, target, mode });
      toast.success(`Transferred. Customer DID preserved: ${data.preserved_did}`);
    } catch (e) {
      toast.error("Transfer failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const addConferenceParticipant = async (participant) => {
    if (!activeCall?.sid) { toast.error("No active Twilio call"); return; }
    try {
      await api.post("/twilio/conference/add", { call_sid: activeCall.sid, participant });
      toast.success("Conference participant added");
    } catch (e) {
      toast.error("Conference add failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <CallCtx.Provider value={{
      activeCall, duration, muted, held, recording, dialerOpen, deviceReady, incomingCall,
      lastCallError,
      setMuted: setMutedReal, setHeld, setRecording, setDialerOpen,
      startCall, endCall, acceptIncoming, rejectIncoming, sendDigits,
      transferCall, addConferenceParticipant,
    }}>
      {children}
    </CallCtx.Provider>
  );
}

export const useCall = () => useContext(CallCtx);

export function formatDuration(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}
